import { t, Trans } from '@lingui/macro';
import PropTypes from 'prop-types';
import React from "react";

import { I18nProviderWrapper } from '@components/common/I18nProviderWrapper';

import './styles/clipboard_copy.scss';

export default class ClipboardCopy extends React.Component {
  state = {
    copied: false,
    srLiveText: ''
  };

  componentWillUnmount() {
    if (this._srAnnounceTimeout) {
      window.clearTimeout(this._srAnnounceTimeout);
    }
  }

  announceCopySuccess() {
    const message = this.props.copySuccessAnnouncement ?? t`Copied to clipboard`;
    if (this._srAnnounceTimeout) {
      window.clearTimeout(this._srAnnounceTimeout);
    }
    this.setState({ srLiveText: '' });
    this._srAnnounceTimeout = window.setTimeout(() => {
      this.setState({ srLiveText: message });
      this._srAnnounceTimeout = undefined;
    }, 100);
  }

  copyToClipboard(e) {
    // Prevent default behavior for keyboard events
    if (e && e.type === 'keydown') {
      e.preventDefault();
    }

    const { onCopy, textToCopy } = this.props;

    // Because document.execCommand('copy') is deprecated, it has been replaced with the clipboard from the Navigator object.
    // IMPORTANT: Navigator.clipboard only works if you're on localhost or on a secure (https) domain.
    // You will have to modify your chrome flags so lvh.me enables the clipboard to copy.
    // INSTRUCTIONS:
    // 1. As a shortcut, in your browser, type: chrome://flags/
    // 2. Search for 'Insecure origins treated as secure'
    // 3. In the input, you will type: 'http://lvh.me:3000' (MAKE SURE THIS IS ENABLED)
    // 4. This should now enable it!

    if (!window.navigator?.clipboard) {
      const textField = document.createElement('textarea');
      textField.innerText = textToCopy;

      document.body.appendChild(textField);
      textField.select();

      document.execCommand('copy');

      textField.remove();

      if (onCopy) {
        onCopy();
      }

      this.announceCopySuccess();
      this.setState({ copied: true }, () => setTimeout(() => this.setState({ copied: false }), 2000));
    } else {
      window.navigator.clipboard.writeText(textToCopy).then(() => {

        if (onCopy) {
          onCopy();
        }

        this.announceCopySuccess();
        this.setState({ copied: true }, () => setTimeout(() => this.setState({ copied: false }), 2000));
      });
    }
  }

  handleKeyDown(e) {
    // Handle Enter and Space keys for keyboard accessibility
    if (e.key === 'Enter' || e.key === ' ') {
      this.copyToClipboard(e);
    }
  }

  renderFeedbackAsTooltip() {
    if (this.state.copied) {
      return (
        <span className="feedback-as-tooltip-container">
          <span className="feedback-as-tooltip"><Trans>Copied!</Trans></span>
          {this.props.children}
        </span>
      );
    }

    return this.props.children;
  }

  renderFeedbackAsReplacement() {
    if (this.state.copied) {
      return <span aria-label="copied" className="bff-check-mark feedback-as-replacement" />;
    }

    return this.props.children;
  }

  render() {
    return (
      <I18nProviderWrapper>
        <>
          <span
            aria-atomic="true"
            aria-live="polite"
            className="sr-only"
            role="status"
          >
            {this.state.srLiveText}
          </span>
          <span
            aria-label={this.props.ariaLabel}
            className={`clipboard-copy-component ${this.props.needIcon ? `bff-copy` : ''}`}
            onClick={(e) => this.copyToClipboard(e)}
            onKeyDown={(e) => this.handleKeyDown(e)}
            rel={this.props.needIcon ? "tooltip" : undefined}
            role="button"
            tabIndex={0}
            title={this.props.needIcon ? "Copy to Clipboard" : undefined}
          >
            {this.props.showFeedbackAsTooltip
              ? this.renderFeedbackAsTooltip()
              : this.renderFeedbackAsReplacement()
            }
          </span>
        </>
      </I18nProviderWrapper>
    );
  }
}

ClipboardCopy.propTypes = {
  ariaLabel: PropTypes.string, // eslint-disable-line react/require-default-props
  copySuccessAnnouncement: PropTypes.string, // eslint-disable-line react/require-default-props
  needIcon: PropTypes.bool,
  onCopy: PropTypes.func, // eslint-disable-line react/require-default-props
  showFeedbackAsTooltip: PropTypes.bool,
  textToCopy: PropTypes.string
};

ClipboardCopy.defaultProps = {
  needIcon: false,
  showFeedbackAsTooltip: true,
  textToCopy: null
};
